import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Certificate, { printCertificate } from "@/components/Certificate";
import { ArrowLeft, Award, Printer, Lock, Loader2, ToggleLeft, ToggleRight } from "lucide-react";

export default function CertificatesPage({ mode = "self" }) {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = mode === "self" ? await api.myCertificates() : await api.studentCertificates(studentId);
      setData(res);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode, studentId]);

  const onPrint = async (cert) => {
    printCertificate({ cert, studentName: data?.student?.name });
    try { await api.markCertificatePrinted(cert.id); load(); } catch { /* ignore */ }
  };

  const toggleOwnerPref = async () => {
    setSaving(true);
    try {
      const next = !(data?.settings?.owner_enabled);
      await api.setMyCertPref(next);
      toast.success(next ? "Printable certificates enabled" : "Printable certificates disabled");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const s = data?.settings || {};
  const isAdult = mode !== "self";
  const canPrint = !!s.can_print || isAdult; // adults can always print; students only if enabled

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
      <button onClick={() => navigate(-1)} data-testid="cert-back"
        className="mb-4 inline-flex items-center gap-2 text-sm font-bold text-[#6B7280] hover:text-[#1F2937]">
        <ArrowLeft size={16} /> Back
      </button>

      <header className="flex flex-wrap items-start gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-[#0D9488]/10 text-[#0D9488]"><Award size={28} /></div>
        <div className="flex-1 min-w-[220px]">
          <h1 className="font-heading text-3xl font-extrabold text-[#1F2937]">
            {mode === "self" ? "My Certificates" : `Certificates · ${data?.student?.name || ""}`}
          </h1>
          <p className="mt-1 font-body text-sm text-[#6B7280]">
            Every completed lesson, subject and cursive level earns a beautifully printable certificate.
          </p>
        </div>

        {isAdult && (
          <button onClick={toggleOwnerPref} disabled={saving} data-testid="toggle-cert-print"
            className={`inline-flex items-center gap-2 rounded-full border-2 px-4 py-2.5 font-heading font-bold ${s.owner_enabled ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]" : "border-[#E5DECF] bg-white text-[#6B7280]"}`}>
            {saving ? <Loader2 size={16} className="animate-spin" /> : (s.owner_enabled ? <ToggleRight size={20} /> : <ToggleLeft size={20} />)}
            {s.owner_enabled ? "Printing enabled" : "Printing disabled"}
          </button>
        )}
      </header>

      {!s.platform_enabled && (
        <div className="mt-6 rounded-2xl border border-[#E05A47]/30 bg-[#E05A47]/5 p-4 text-sm text-[#B4402F]" data-testid="cert-platform-disabled">
          <strong>Printing is currently disabled by ZoodLeap.</strong> Students can still see certificates they've earned; the print button will return when the platform admin re-enables it.
        </div>
      )}
      {s.platform_enabled && !s.owner_enabled && mode === "self" && (
        <div className="mt-6 rounded-2xl border border-[#FF9E1B]/40 bg-[#FF9E1B]/10 p-4 text-sm text-[#B45309]" data-testid="cert-owner-disabled">
          Ask your teacher or parent to enable printing — your certificates are safe here in the meantime!
        </div>
      )}

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#0D9488]" /></div>
      ) : (data?.certificates || []).length === 0 ? (
        <div className="mt-10 rounded-3xl border-2 border-dashed border-[#E5DECF] bg-white p-12 text-center" data-testid="cert-empty">
          <Award className="mx-auto text-[#0D9488]" size={40} />
          <h2 className="mt-3 font-heading text-xl font-bold text-[#1F2937]">No certificates yet</h2>
          <p className="mt-1 font-body text-sm text-[#6B7280]">
            {mode === "self"
              ? "Finish your first lesson to earn a shiny certificate you can print and hang up!"
              : "Certificates will show up here as this student completes lessons, subjects and cursive levels."}
          </p>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 lg:grid-cols-1" data-testid="cert-list">
          {data.certificates.map((c) => (
            <div key={c.id} className="rounded-3xl bg-white p-4 shadow-sm border border-[#E5DECF]" data-testid={`cert-card-${c.id}`}>
              <Certificate cert={c} studentName={data.student?.name} />
              <div className="mt-3 flex items-center justify-between">
                <div className="text-xs font-body text-[#6B7280]">
                  {c.printed_at ? <>Last printed {new Date(c.printed_at).toLocaleDateString()}</> : <>Ready to print</>}
                </div>
                {canPrint ? (
                  <button onClick={() => onPrint(c)} data-testid={`cert-print-${c.id}`}
                    className="inline-flex items-center gap-2 rounded-full bg-[#0D9488] px-5 py-2 font-heading font-bold text-white hover:bg-[#0B7F74]">
                    <Printer size={16} /> Print
                  </button>
                ) : (
                  <div className="inline-flex items-center gap-2 rounded-full border-2 border-[#E5DECF] bg-white px-5 py-2 text-sm font-bold text-[#94A3B8]" data-testid={`cert-print-locked-${c.id}`}>
                    <Lock size={14} /> Print disabled
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
